import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FieldErrors, useForm } from "react-hook-form";

import { MessageResponseDto } from "@api/api";
import { useFormApiMapper } from "@factory/services/form-api-mapper.hook";
import { useQyCreatePatient } from "@factory/query/patient.query";
import { PatientFormRule, PatientFormSchema } from "@factory/form/form.rule";
import { patientFormDefault } from "@factory/form/form.default";
import { getFormErrorMessage } from "@core/utility/get-error-message";
import { useNotificationContext } from "@shared/ui/notification/notification.context";
import { useUserIdentityContext } from "@core/services/user-identity.context";

export function useNewPatient() {
  // LOCAL DECLARATION
  const navigate = useNavigate();
  const { mapCreatePatient } = useFormApiMapper();
  const { showError, showSuccess, showWarning } = useNotificationContext();
  const { getUserRole } = useUserIdentityContext();

  // QUERIES
  const handleApiSuccess = (response: MessageResponseDto) => {
    const data = response.data as any;

    showSuccess(`New Patient is created`);
    const name = `${data?.firstName} ${data?.lastName}`;
    navigate(`../../contract/new?code=${data?.code}&name=${name}`);
  };
  const { mutate: addPatient, isLoading: isCreating } =
    useQyCreatePatient(handleApiSuccess);

  // LOCAL FUNCTIONS
  const formMethod = useForm<PatientFormSchema>({
    defaultValues: patientFormDefault,
    resolver: zodResolver(PatientFormRule),
  });
  const { handleSubmit } = formMethod;

  const handleValidate = (form: PatientFormSchema) => {
    const { tenant } = getUserRole();
    const updatedForm = {
      ...form,
      tenant,
    };
    const formData = mapCreatePatient(updatedForm);
    addPatient(formData);
  };
  const handleValidateError = (err: FieldErrors<PatientFormSchema>) => {
    const formMessage = getFormErrorMessage(err);
    showError(formMessage);
  };

  return {
    isCreating,
    formMethod,
    navigate,
    handleSubmit,
    handleValidate,
    handleValidateError,
  };
}
